import { Fragment, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { billingApi, messagingApi } from '@/api/ops';
import type { AdminAuditEntry, AuditSeverity } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Segmented, Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { TextField } from '@/components/ops/Forms';
import { LoadMore, PageHeader, Section, SubTabs } from '@/components/ops/Layout';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { QueryState } from '@/components/ops/Problem';
import { BILLING_AUDIT_ACTION_LABELS, auditActionLabel, isSecurityEntry, isSystemActor, parseAuditSeverity, type AuditSource } from '@/ops/auditActions';
import { formatTimestamp, shortId } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { useSelectedEnvironment } from '@/ops/useEnvironment';
import { TenantName, TenantPicker } from '@/components/ops/TenantPicker';

type Source = AuditSource;
type SeverityChoice = AuditSeverity | 'all';


/**
 * The services' own admin audit logs — every console write, with the acting admin (stamped from the
 * console JWT, CP5), the reason, and the redacted before/after — plus billing's automated money
 * transitions, written by a System actor. Read-only, live. Billing marks credential and API-key writes
 * `security`; the severity filter is billing's `?severity=` (kept in this page's URL so a link to "billing
 * security events" opens filtered).
 */
export function AuditPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const [params, setParams] = useSearchParams();
  const canBilling = hasPermission(PERMISSIONS.billingRead);
  const canMessaging = hasPermission(PERMISSIONS.messagingRead);
  const requested = params.get('log') as Source | null;
  const source: Source = requested === 'messaging' && canMessaging ? 'messaging' : canBilling ? 'billing' : 'messaging';
  // Only the two values billing accepts ever leave this page; anything else in the URL reads as "all".
  const severity = parseAuditSeverity(params.get('severity'));
  const update = (key: string, value: string | null) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value); else next.delete(key);
    return next;
  }, { replace: true });

  return (
    <RequirePermission anyOf={[PERMISSIONS.billingRead, PERMISSIONS.messagingRead]} what="The admin audit logs">
      <PageHeader title="Activity log" subtitle="Who changed what in billing and messaging from the console, and why — and what billing's workers did on their own."
        actions={<EnvironmentPicker env={env} environments={environments} onChange={setEnv} />} />
      <SubTabs<Source> label="Audit source" active={source} onChange={(s) => update('log', s)} tabs={[
        { key: 'billing', label: 'Billing', hidden: !canBilling },
        { key: 'messaging', label: 'Messaging', hidden: !canMessaging },
      ]} />
      {env && (
        <AuditLog key={`${env}-${source}`} env={env} source={source}
          severity={source === 'billing' ? severity : null}
          onSeverity={(s) => update('severity', s === 'all' ? null : s)} />
      )}
    </RequirePermission>
  );
}

function AuditLog({
  env, source, severity, onSeverity,
}: {
  env: string;
  source: Source;
  severity: AuditSeverity | null;
  onSeverity: (value: SeverityChoice) => void;
}) {
  const [tenantId, setTenantId] = useState('');
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const tenantOk = true;

  const log = useInfiniteQuery({
    queryKey: ['ops', source, env, 'audit', tenantId, action, targetType, severity],
    queryFn: ({ pageParam }) => source === 'billing'
      ? billingApi.auditLog(env, {
        tenant_id: tenantId.trim() || undefined,
        action: action.trim() || undefined,
        target_type: targetType.trim() || undefined,
        severity: severity ?? undefined,
        cursor: pageParam,
      })
      // Messaging's log has no severity, action or target filter (its endpoint takes tenant_id only).
      : messagingApi.auditLog(env, { tenant_id: tenantId.trim() || undefined, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: tenantOk,
    ...LIVE,
  });
  const rows = log.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <Section title={`${source === 'billing' ? 'Billing' : 'Messaging'} admin log`} meta="newest first" flush>
      <div className="card-body">
        <div className="filter-grid">
          {source === 'billing' && (
            <Segmented<SeverityChoice> label="Severity" value={severity ?? 'all'} onChange={onSeverity}
              options={[{ value: 'all', label: 'All' }, { value: 'security', label: 'Security' }, { value: 'info', label: 'Info' }]} />
          )}
          <TenantPicker env={env} value={tenantId} onChange={setTenantId} />
          {source === 'billing' && (
            <Select label="What happened" value={action} onChange={setAction} placeholder="Anything"
              options={Object.entries(BILLING_AUDIT_ACTION_LABELS).map(([value, label]) => ({ value, label, hint: value }))} />
          )}
          {source === 'billing' && <TextField label="Target type" value={targetType} onChange={setTargetType} mono placeholder="offer" />}
        </div>
      </div>
      {tenantOk && (
        <QueryState query={{ ...log, data: log.data ? rows : undefined }} service={source}>
          {(entries) => entries.length === 0 ? (
            <div className="empty-state t-body">No entries for this filter.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>When</th><th>Admin</th><th>Action</th><th>Target</th><th>Tenant</th><th>Reason</th><th aria-label="Details" /></tr></thead>
                <tbody>
                  {entries.map((e: AdminAuditEntry) => (
                    <Fragment key={e.id}>
                      <tr data-severity={isSecurityEntry(e) ? 'security' : 'info'}>
                        <td className="t-caption">{formatTimestamp(e.at)}</td>
                        <td className="t-caption">
                          {isSystemActor(e) ? (
                            <>
                              <StatusChip status="system" tone="muted" title="An automated worker, not a console admin." />
                              {e.actor_name && <div className="muted mono">{e.actor_name}</div>}
                            </>
                          ) : (
                            <>{e.actor_name ?? '—'}<div className="muted mono">{shortId(e.actor_id)}</div></>
                          )}
                        </td>
                        <td className="t-caption"><ActionCell entry={e} source={source} /></td>
                        <td className="t-caption">{e.target_type}{e.target_id && <div className="muted mono">{shortId(e.target_id)}</div>}</td>
                        <td className="t-caption">{e.tenant_id ? <TenantName env={env} id={e.tenant_id} /> : '—'}</td>
                        <td className="t-caption wrap">{e.reason ?? '—'}</td>
                        <td>
                          {(e.before !== null || e.after !== null) && (
                            <button type="button" className="btn btn-ghost btn-sm" aria-expanded={open === e.id} onClick={() => setOpen(open === e.id ? null : e.id)}>
                              {open === e.id ? 'Hide' : 'Before / after'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {open === e.id && (
                        <tr>
                          <td colSpan={7}>
                            <div className="viz-pair">
                              <div><div className="t-label muted">Before (redacted)</div><pre className="pre-json">{JSON.stringify(e.before, null, 2)}</pre></div>
                              <div><div className="t-label muted">After (redacted)</div><pre className="pre-json">{JSON.stringify(e.after, null, 2)}</pre></div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryState>
      )}
      <LoadMore hasMore={!!log.hasNextPage} loading={log.isFetchingNextPage} onLoad={() => log.fetchNextPage()} />
    </Section>
  );
}

/** The readable label, the raw action string under it (what the Action filter matches), and the security badge. */
function ActionCell({ entry, source }: { entry: AdminAuditEntry; source: Source }) {
  const label = auditActionLabel(entry.action, source);
  const security = isSecurityEntry(entry);
  return (
    <div>
      <div className="row" style={{ gap: 6 }}>
        {label ? <span className="t-body">{label}</span> : <span className="mono">{entry.action}</span>}
        {security && <StatusChip status="security" tone="warning" title="A credential, API-key, gateway or webhook write." />}
      </div>
      {label && <div className="muted mono">{entry.action}</div>}
    </div>
  );
}
